import { Box, Typography, Card, IconButton, Tooltip, useMediaQuery, Theme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useRecordContext, useNotify } from "react-admin";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { useAccountProject } from "../../../contexts/AccountProjectContext";
import AnimatedBlock from "../../../components/Common/AnimatedBlock";
import InfoRow from "../../../components/Common/InfoRow";
import { StatusLabel } from "../../../components/Label/status";
import { formatDate, formatDateTime, linkAmount } from "../../../utils/paymentFormat";
import { linkCurrencyLabel } from "./linkCurrency";

const SummaryTab = () => {
  const { t }: { t: any } = useTranslation();
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down("sm"));
  const notify = useNotify();
  const record = useRecordContext();
  const { projects } = useAccountProject();
  if (!record) return null;

  const params = record.params || {};
  // Entity names come from the entities already loaded for the entity switcher.
  const projectId = record.projectId || params.projectId;
  const entity = projects.find((project) => project.id === projectId);
  const copyLink = () => {
    navigator.clipboard.writeText(record.url);
    notify(t("Link copied to clipboard"), { type: "success" });
  };

  return (
    <Box>
      {!isMobile && (
        <Box pb={2}>
          <Typography variant="h6">
            {t("Summary")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("Reference, amount, entity and dates of this payment link")}
          </Typography>
        </Box>
      )}

      <AnimatedBlock>
        <Card>
          <Box px={2} pt={2}>
            <Typography variant="overline" color="text.secondary">
              {t("Payment link")}
            </Typography>
          </Box>
          <Box px={2} pb={1}>
            <InfoRow label={t("Reference")}>{record.internalRef}</InfoRow>
            <InfoRow label={t("Status")}>
              <StatusLabel fontSize="small" statusField="statusPaymentLink" />
            </InfoRow>
            <InfoRow label={t("Amount")}>{linkAmount(params)}</InfoRow>
            <InfoRow label={t("Payment currency")}>{linkCurrencyLabel(params.currency, t)}</InfoRow>
            <InfoRow label={t("Entity")}>
              {entity?.name || "—"}
            </InfoRow>
            <InfoRow label={t("Created date")}>{formatDateTime(record.dateCreated)}</InfoRow>
            <InfoRow label={t("Expiration date")}>{formatDate(record.expiresAt)}</InfoRow>
            {record.url && (
              <InfoRow label={t("Link")}>
                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                  <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 400 }}>
                    {record.url}
                  </Typography>
                  <Tooltip title={t("Copy link")}>
                    <IconButton size="small" onClick={copyLink} aria-label={t("Copy link")}>
                      <ContentCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </InfoRow>
            )}
          </Box>
        </Card>
      </AnimatedBlock>
    </Box>
  );
};

export default SummaryTab;
